import type { Type } from '@angular/core';
import type { DxPanelGroup } from '@kerberos-compliance/compliance-shared-ui/dx';

/**
 * The tab layout of AML Desk's DX drawer, handed to the shared shell in
 * `compliance-shared-ui`. The shell owns the chrome - trigger, drawer, tab bar, load
 * states - and knows nothing about which panels exist; this file is the whole of what
 * AML Desk adds to it.
 *
 * A group is visible only while one of its `tools` is enabled, and each panel renders
 * only while its own `tool` is. Both are checked against the same gate, so a tab can
 * never be reachable with every panel behind it gated off, or the reverse.
 *
 * Every `load` stays a dynamic import: a panel's chunk is fetched the first time its
 * tab is opened, so a session that never opens the drawer downloads none of them.
 *
 * Four of these are now the *shared* panel rather than an app copy - grants, storage,
 * status and Sentry. Where a panel answers a question every frontend has, an app-local
 * copy is how two of them come to answer it differently: Admin Desk's grants panel
 * disclosed a whole decoded JWT, and its storage panel copied raw values to the
 * clipboard, while the versions here did neither. What stays local is what only this app
 * can know: Hanko, the login flow, the inactivity clock, its stores, its request path.
 */
export const dxPanels: DxPanelGroup[] = [
  {
    id: 'auth',
    label: '🔑 Auth & Grants',
    tools: ['auth-explainer'],
    panels: [
      {
        // The shared panel, fed by this app's `AuthStore` through the grants context in
        // `dx.providers.ts`. Nothing about role precedence is re-derived in DX code.
        tool: 'auth-explainer',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
          return module.DxGrantsPanelComponent;
        },
      },
    ],
  },
  {
    id: 'session',
    label: '⏱️ Session & Hanko',
    tools: ['session-inspector', 'hanko-inspector', 'hanko-flow-timeline'],
    panels: [
      {
        tool: 'session-inspector',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('./components/panels/dx-session-panel/dx-session-panel.component');
          return module.DxSessionPanelComponent;
        },
      },
      {
        tool: 'hanko-inspector',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('./components/panels/dx-hanko-panel/dx-hanko-panel.component');
          return module.DxHankoPanelComponent;
        },
      },
      {
        tool: 'hanko-flow-timeline',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('./components/panels/dx-hanko-flow-panel/dx-hanko-flow-panel.component');
          return module.DxHankoFlowPanelComponent;
        },
      },
    ],
  },
  {
    id: 'state',
    label: '🧠 State & Signals',
    tools: ['state-snapshot'],
    panels: [
      {
        tool: 'state-snapshot',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('./components/panels/dx-state-panel/dx-state-panel.component');
          return module.DxStatePanelComponent;
        },
      },
    ],
  },
  {
    id: 'storage',
    label: '💾 Storage & Reset',
    tools: ['storage-inspector'],
    panels: [
      {
        // The shared panel, under this app's redaction policies. It also enumerates
        // foreign keys - MSAL's, a hand-set one - which the app copy never did.
        tool: 'storage-inspector',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
          return module.DxStoragePanelComponent;
        },
      },
    ],
  },
  {
    id: 'network',
    label: '📡 Network Overrides',
    tools: ['network-interceptor'],
    panels: [
      {
        tool: 'network-interceptor',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('./components/panels/dx-network-panel/dx-network-panel.component');
          return module.DxNetworkPanelComponent;
        },
      },
    ],
  },
  {
    id: 'a11y',
    label: '♿ a11y & i18n',
    tools: ['a11y-i18n-inspector'],
    panels: [
      {
        tool: 'a11y-i18n-inspector',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
          return module.DxA11yPanelComponent;
        },
      },
    ],
  },
  {
    id: 'system',
    label: '⚙️ Status & Sentry',
    tools: ['diagnostics-bundle', 'sentry-trigger'],
    panels: [
      {
        // The shared status panel: build facts, session state and the login flow, all
        // contributed from `dx-status.contributors.ts`, with one copyable snapshot. It
        // replaced a bespoke diagnostics panel *and* a bespoke build badge, which
        // reported overlapping facts in two shapes.
        tool: 'diagnostics-bundle',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
          return module.DxStatusPanelComponent;
        },
      },
      {
        // The shared panel, not an app copy: its scenarios travel the app's own
        // `ErrorHandlerService`, so what reaches Sentry is normalized exactly as a real
        // failure would be.
        tool: 'sentry-trigger',
        load: async (): Promise<Type<unknown>> => {
          const module = await import('@kerberos-compliance/compliance-shared-ui/dx');
          return module.DxSentryPanelComponent;
        },
      },
    ],
  },
];
